import React, { useState, useEffect } from "react";
import { useAuth } from "./hooks/useAuth";
import Dashboard from "./components/Dashboard";
import LocalDashboard from "./components/LocalDashboard";
import LandingPage from "./components/LandingPage";
import Login from "./components/auth/Login";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";

function routeFromPath(pathname) {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/dashboard") return "dashboard";
  if (normalized === "/login") return "login";
  if (normalized === "/signup") return "signup";
  if (normalized === "/forgot-password") return "forgotPassword";
  if (normalized === "/reset-password") return "resetPassword";
  return "landing";
}

function pathFromRoute(route) {
  if (route === "dashboard") return "/dashboard";
  if (route === "login") return "/login";
  if (route === "signup") return "/signup";
  if (route === "forgotPassword") return "/forgot-password";
  if (route === "resetPassword") return "/reset-password";
  return "/";
}

function App() {
  const appMode = import.meta.env.VITE_APP_MODE || "supabase";
  const useSupabaseMode = appMode === "supabase";
  const { user, loading, hasSupabaseEnv } = useAuth();
  const [route, setRoute] = useState(() => routeFromPath(window.location.pathname));

  const navigateTo = (nextRoute, { replace = false } = {}) => {
    const nextPath = pathFromRoute(nextRoute);
    if (window.location.pathname !== nextPath) {
      if (replace) {
        window.history.replaceState({}, "", nextPath);
      } else {
        window.history.pushState({}, "", nextPath);
      }
    }
    setRoute(nextRoute);
  };

  useEffect(() => {
    const onPopState = () => {
      setRoute(routeFromPath(window.location.pathname));
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // In Supabase mode, authenticated users should always land on dashboard.
  useEffect(() => {
    if (!useSupabaseMode) return;
    if (user && !["dashboard", "resetPassword"].includes(route)) {
      navigateTo("dashboard", { replace: true });
    }
  }, [user, route, useSupabaseMode]);

  // Unauthenticated users cannot stay on dashboard route.
  useEffect(() => {
    if (!useSupabaseMode) return;
    if (!user && route === "dashboard") {
      navigateTo("login", { replace: true });
    }
  }, [user, route, useSupabaseMode]);

  if (!useSupabaseMode) {
    return <LocalDashboard />;
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--background)'
      }}>
        Loading...
      </div>
    );
  }

  if (!hasSupabaseEnv) {
    return (
      <div style={{
        maxWidth: '760px',
        margin: '48px auto',
        padding: '24px',
        border: '1px solid #f5c2c7',
        borderRadius: '12px',
        background: '#fff3f4',
        color: '#58151c',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif'
      }}>
        <h2 style={{ margin: '0 0 12px 0' }}>Supabase setup required</h2>
        <p style={{ margin: '0 0 12px 0' }}>
          Create <code>.env</code> in the project root with:
        </p>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
VITE_SUPABASE_URL=https://your-project-id.supabase.co{'\n'}
VITE_SUPABASE_ANON_KEY=your-anon-key
        </pre>
        <p style={{ margin: '12px 0 0 0' }}>
          Then restart the dev server with <code>npm run dev</code>.
        </p>
      </div>
    );
  }

  const renderView = () => {
    switch (route) {
      case "dashboard":
        return user ? <Dashboard /> : <Login mode="signin" onSwitchMode={() => navigateTo("signup")} onBackHome={() => navigateTo("landing")} />;
      case "login":
        return (
          <Login
            mode="signin"
            onSwitchMode={() => navigateTo("signup")}
            onBackHome={() => navigateTo("landing")}
            onForgotPassword={() => navigateTo("forgotPassword")}
          />
        );
      case "signup":
        return <Login mode="signup" onSwitchMode={() => navigateTo("login")} onBackHome={() => navigateTo("landing")} />;
      case "forgotPassword":
        return (
          <ForgotPassword
            onBackToLogin={() => navigateTo("login")}
            onBackHome={() => navigateTo("landing")}
          />
        );
      case "resetPassword":
        return (
          <ResetPassword
            onBackToLogin={() => navigateTo("login")}
            onBackHome={() => navigateTo("landing")}
          />
        );
      case "landing":
      default:
        return (
          <LandingPage
            onLoginClick={() => navigateTo("login")}
            onSignupClick={() => navigateTo("signup")}
            onDashboardClick={() => navigateTo("dashboard")}
          />
        );
    }
  };

  return (
    <>
      {renderView()}
    </>
  );
}

export default App;
